/**
 * AdminWaitlistPage — pre-launch demand roster (Part E, 2026-07-21).
 *
 * Admin-only. Lists every waitlist row with filters (ZIP, source,
 * marketing_opt_in, opted_in_only, include_demo, email search) and
 * exports CSV via the same `csvDownload.js` helper Contest A uses.
 *
 * All access is via the `require_admin` backend dependency; this
 * page is mounted behind the admin route guard in `App.js`.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Loader2, Download } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { downloadCsv } from "@/lib/csvDownload";

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" });
}

export default function AdminWaitlistPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const [zipFilter, setZipFilter] = useState("");
  const [sourceFilter, setSourceFilter] = useState("");
  const [q, setQ] = useState("");
  const [optedInOnly, setOptedInOnly] = useState(false);
  const [includeDemo, setIncludeDemo] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        limit: 200,
        offset: 0,
        opted_in_only: optedInOnly,
        include_demo: includeDemo,
        sort: "created_at_desc",
      };
      if (zipFilter.trim()) params.zip = zipFilter.trim();
      if (sourceFilter.trim()) params.source = sourceFilter.trim();
      if (q.trim()) params.q = q.trim();
      const r = await api.get("/admin/waitlist", { params });
      setRows(r.data?.rows || []);
      setTotal(r.data?.total || 0);
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Failed to load waitlist.",
      );
    } finally {
      setLoading(false);
    }
  }, [zipFilter, sourceFilter, q, optedInOnly, includeDemo]);

  useEffect(() => {
    load();
  }, [load]);

  async function handleExportCsv() {
    setExporting(true);
    try {
      const params = {
        opted_in_only: optedInOnly,
        include_demo: includeDemo,
      };
      if (zipFilter.trim()) params.zip = zipFilter.trim();
      if (sourceFilter.trim()) params.source = sourceFilter.trim();
      if (q.trim()) params.q = q.trim();
      await downloadCsv("/admin/waitlist/export.csv", { params });
    } catch (err) {
      toast.error(
        formatApiErrorDetail(err?.response?.data?.detail) ||
          "Couldn't export CSV.",
      );
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="space-y-6" data-testid="admin-waitlist-page">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-pp-text">
            <span className="text-brand-orange">Waitlist</span> — pre-launch demand
          </h1>
          <p className="text-sm text-muted-foreground">
            Total on list: <strong data-testid="admin-waitlist-total">{total}</strong>
          </p>
        </div>
        <Button
          type="button"
          onClick={handleExportCsv}
          disabled={exporting || loading}
          className="rounded-full bg-pp-purple text-white hover:bg-pp-purple-dark"
          data-testid="admin-waitlist-export-csv-button"
        >
          {exporting ? (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          ) : (
            <Download className="mr-2 h-4 w-4" />
          )}
          Export CSV
        </Button>
      </div>

      <div className="rounded-2xl border border-border bg-pp-surface p-4">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div>
            <Label htmlFor="wl-filter-zip" className="text-xs font-semibold text-pp-text">ZIP</Label>
            <Input
              id="wl-filter-zip"
              value={zipFilter}
              onChange={(e) => setZipFilter(e.target.value)}
              placeholder="e.g. 68022"
              className="mt-1 h-10 rounded-full border-border bg-pp-surface-2 px-4"
              data-testid="admin-waitlist-filter-zip"
            />
          </div>
          <div>
            <Label htmlFor="wl-filter-source" className="text-xs font-semibold text-pp-text">Source</Label>
            <Input
              id="wl-filter-source"
              value={sourceFilter}
              onChange={(e) => setSourceFilter(e.target.value)}
              placeholder="e.g. landing"
              className="mt-1 h-10 rounded-full border-border bg-pp-surface-2 px-4"
              data-testid="admin-waitlist-filter-source"
            />
          </div>
          <div className="sm:col-span-2">
            <Label htmlFor="wl-filter-q" className="text-xs font-semibold text-pp-text">Search (email)</Label>
            <Input
              id="wl-filter-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="partial match"
              className="mt-1 h-10 rounded-full border-border bg-pp-surface-2 px-4"
              data-testid="admin-waitlist-filter-q"
            />
          </div>
        </div>
        <div className="mt-3 flex flex-wrap gap-6">
          <label className="flex items-center gap-2 text-sm text-pp-text">
            <Checkbox
              checked={optedInOnly}
              onCheckedChange={(v) => setOptedInOnly(v === true)}
              data-testid="admin-waitlist-opted-in-only-checkbox"
            />
            Opted-in only
          </label>
          <label className="flex items-center gap-2 text-sm text-pp-text">
            <Checkbox
              checked={includeDemo}
              onCheckedChange={(v) => setIncludeDemo(v === true)}
              data-testid="admin-waitlist-include-demo-checkbox"
            />
            Include demo entries
          </label>
        </div>
      </div>

      {loading ? (
        <div
          className="flex items-center justify-center rounded-2xl border border-border bg-pp-surface p-10"
          data-testid="admin-waitlist-loading"
        >
          <Loader2 className="h-5 w-5 animate-spin text-brand-orange" />
        </div>
      ) : rows.length === 0 ? (
        <div
          className="rounded-2xl border border-border bg-pp-surface p-8 text-center"
          data-testid="admin-waitlist-empty"
        >
          <p className="text-sm font-semibold text-pp-text">No matching entries.</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Adjust filters or wait for more signups.
          </p>
        </div>
      ) : (
        <div
          className="overflow-x-auto rounded-2xl border border-border bg-pp-surface"
          data-testid="admin-waitlist-list"
        >
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-semibold uppercase text-muted-foreground">
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">ZIP</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Joined</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Marketing</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  key={r.id}
                  className="border-t border-border"
                  data-testid={`admin-waitlist-row-${r.id}`}
                >
                  <td className="px-4 py-3 text-pp-text">{r.name}</td>
                  <td className="px-4 py-3 text-pp-text">{r.email}</td>
                  <td className="px-4 py-3 text-pp-text">{r.zip}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.phone || "—"}</td>
                  <td className="px-4 py-3 text-muted-foreground">{formatDate(r.created_at)}</td>
                  <td className="px-4 py-3 text-muted-foreground">{r.source || "—"}</td>
                  <td className="px-4 py-3">
                    {r.marketing_opt_in ? (
                      <span className="rounded-full bg-pp-surface-2 px-2 py-0.5 text-xs font-semibold text-brand-orange">
                        Yes
                      </span>
                    ) : (
                      <span className="text-xs text-muted-foreground">No</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
